// dashboard/components/TelegramSignals.jsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Avatar,
  Paper,
  LinearProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Telegram,
  CheckCircle,
  Warning,
  SignalCellularAlt,
  Refresh,
  Link as LinkIcon,
  CopyAll,
  Check,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';

const TelegramSignals = () => {
  const [isConnected, setIsConnected] = useState(true);
  const [isCopied, setIsCopied] = useState(false);

  const signals = [
    { id: 1, pair: 'EUR/USD', type: 'buy', entry: '1.08750', sl: '1.08500', tp: '1.09200', status: 'active', confidence: 85, time: '14:30' },
    { id: 2, pair: 'BTC/USD', type: 'sell', entry: '42,500', sl: '43,000', tp: '41,000', status: 'active', confidence: 78, time: '13:45' },
    { id: 3, pair: 'ETH/USD', type: 'buy', entry: '2,250', sl: '2,200', tp: '2,350', status: 'completed', confidence: 92, time: '10:00' },
  ];

  const handleCopyLink = () => {
    navigator.clipboard.writeText('https://t.me/protradesinsight_signals');
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const getStatusColor = (status) => {
    return status === 'active' ? '#2ecc71' : '#3498db';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            Telegram Signals
          </Typography>
          <Typography variant="body2" sx={{ color: '#8a8a9a' }}>
            Real-time trading signals delivered via Telegram
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            icon={isConnected ? <CheckCircle sx={{ fontSize: 14 }} /> : <SignalCellularAlt sx={{ fontSize: 14 }} />}
            label={isConnected ? 'Connected' : 'Disconnected'}
            sx={{
              bgcolor: isConnected ? 'rgba(46, 204, 113, 0.1)' : 'rgba(231, 76, 60, 0.1)',
              color: isConnected ? '#2ecc71' : '#e74c3c',
              fontWeight: 500,
            }}
          />
          <Button
            variant="contained"
            startIcon={<Telegram />}
            sx={{
              bgcolor: '#0088cc',
              '&:hover': { bgcolor: '#006699' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Join Channel
          </Button>
        </Box>
      </Box>

      {/* Channel Info */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #eef2f7', boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#0088cc', width: 40, height: 40 }}>
            <Telegram sx={{ color: '#fff', fontSize: 22 }} />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
              ProTradesInsight Signals
            </Typography>
            <Typography variant="caption" sx={{ color: '#8a8a9a', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LinkIcon sx={{ fontSize: 14 }} />
              t.me/protradesinsight_signals
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={isCopied ? <Check /> : <CopyAll />}
            onClick={handleCopyLink}
            sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#eef2f7' }}
          >
            {isCopied ? 'Copied!' : 'Copy Link'}
          </Button>
          <FormControlLabel
            control={<Switch checked={isConnected} onChange={() => setIsConnected(!isConnected)} size="small" />}
            label=""
            sx={{ mr: 0 }}
          />
        </Box>
      </Paper>

      {/* Signals Grid */}
      <Grid container spacing={3}>
        {signals.map((signal) => (
          <Grid item xs={12} md={4} key={signal.id}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #eef2f7' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={signal.type.toUpperCase()}
                      size="small"
                      sx={{
                        bgcolor: signal.type === 'buy' ? '#2ecc71' : '#e74c3c',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '10px',
                      }}
                    />
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                      {signal.pair}
                    </Typography>
                  </Box>
                  <Chip
                    label={signal.status}
                    size="small"
                    sx={{
                      bgcolor: `${getStatusColor(signal.status)}15`,
                      color: getStatusColor(signal.status),
                      fontWeight: 500,
                      fontSize: '10px',
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#8a8a9a', display: 'block' }}>Entry</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e' }}>{signal.entry}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#8a8a9a', display: 'block' }}>Stop Loss</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#e74c3c' }}>{signal.sl}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#8a8a9a', display: 'block' }}>Take Profit</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2ecc71' }}>{signal.tp}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#8a8a9a' }}>Confidence</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={signal.confidence}
                      sx={{
                        width: 60,
                        height: 3,
                        borderRadius: 2,
                        bgcolor: '#eef2f7',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: signal.confidence > 80 ? '#2ecc71' : signal.confidence > 60 ? '#f5a623' : '#e74c3c',
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a1a2e' }}>{signal.confidence}%</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#8a8a9a' }}>{signal.time}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default TelegramSignals;